import { useState, type ReactNode } from 'react';

import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import CodeView from '@/designs-dev/inspect/CodeView';
import { type Inspection } from '@/designs-dev/inspect/fiber';
import {
  type ComponentEntry,
  type Sample,
  type ThemeChange,
} from '@/designs-dev/audit/types';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';

/** Code view wants a drill selection; a name is all it reads from one. */
const asInspection = (name: string): Inspection => ({
  name,
  props: [],
  madeOf: [],
  hasSx: false,
  hasVisualSx: false,
  sxText: '',
  hasStyle: false,
  styleText: '',
  domNode: null,
});

/**
 * The phone's width. Components are drawn at it: narrower, a row like the
 * recording bar runs out of room and its buttons land on top of each other.
 */
const PHONE_W = 375;

/** Wide enough for "Before". */
const SWATCH_W = 64;

/** The surface a component is drawn on, the way Storybook frames a story. */
const canvasSx = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
} as const;

/** A box in the grid: the name pinned to the top, the content in the middle. */
const tileSx = {
  ...canvasSx,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  textAlign: 'left',
  overflow: 'hidden',
} as const;

/** Its label in a text colour on its surface: the change, seen rather than named. */
const Swatch = ({ s, label }: { s: Sample; label: string }) => (
  <Box
    sx={{
      height: 28,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      bgcolor: s.bg,
      color: s.ink,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
    }}
  >
    <Typography variant='subtitle2' sx={{ color: 'inherit' }}>
      {label}
    </Typography>
  </Box>
);

/** One change, a line: before, now, its name, and why. */
const ChangeRow = ({ t }: { t: ThemeChange }) => (
  <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(2, ${SWATCH_W}px)`,
        gap: 1,
        flexShrink: 0,
      }}
    >
      {/* A new colour has no before: one swatch across both, with its hex. */}
      {t.was ? (
        <>
          <Swatch s={t.was} label='Before' />
          <Swatch s={t.now} label='Now' />
        </>
      ) : (
        <Box sx={{ gridColumn: '1 / -1', display: 'grid' }}>
          <Swatch s={t.now} label={t.hex ?? 'Now'} />
        </Box>
      )}
    </Box>
    <Stack>
      <Typography variant='body2'>{t.name}</Typography>
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        {t.note}
      </Typography>
    </Stack>
  </Stack>
);

/**
 * Every component a prototype uses, on one page: each drawn on its own, the
 * way Storybook and Chromatic lay them out, with a panel beside the grid that
 * shows the selected one's states and code. The theme changes it has made are
 * the first box, outlined in coral.
 */
const ComponentsPage = ({
  title,
  components,
  themeChanges = [],
}: {
  title: string;
  components: ComponentEntry[];
  themeChanges?: ThemeChange[];
}) => {
  const [open, setOpen] = useState<string | null>(null);
  const current = components.find((c) => c.name === open);
  const pick = (name: string) => setOpen(open === name ? null : name);

  const tile = (name: string, thumbnail: ReactNode) => (
    <ButtonBase
      key={name}
      onClick={() => pick(name)}
      sx={{
        ...tileSx,
        // The open one is marked, so the panel has a source.
        borderColor: open === name ? 'primary.main' : 'divider',
        '&:hover': { borderColor: 'custom.marineGreen.300' },
      }}
    >
      <Typography variant='subtitle2' sx={{ px: 2, pt: 1.5 }}>
        {name}
      </Typography>
      {/* Not interactive here: tapping anywhere opens it. */}
      <Stack
        sx={{
          flex: 1,
          p: 2,
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        {thumbnail}
      </Stack>
    </ButtonBase>
  );

  return (
    <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
      <Stack
        direction='row'
        sx={{ height: '100vh', bgcolor: 'background.default' }}
      >
        {/* Every component. Stays put while one is open beside it. */}
        <Stack
          spacing={3}
          sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 4 }}
        >
          <Typography variant='h2'>{title}</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${PHONE_W + 40}px, 1fr))`,
              gap: 3,
            }}
          >
            {/* A row of its own, in coral: not a component, the theme under them. */}
            {themeChanges.length > 0 && (
              <Box
                sx={{
                  ...tileSx,
                  gridColumn: '1 / -1',
                  borderColor: 'custom.coral.400',
                }}
              >
                <Typography variant='subtitle2' sx={{ px: 2, pt: 1.5 }}>
                  Theme changes
                </Typography>
                <Stack
                  spacing={2}
                  sx={{ flex: 1, p: 2, justifyContent: 'center' }}
                >
                  {themeChanges.map((t) => (
                    <ChangeRow key={t.name} t={t} />
                  ))}
                </Stack>
              </Box>
            )}
            {components.map((c) =>
              tile(
                c.name,
                <Box sx={{ width: PHONE_W }}>{c.states[0].node}</Box>,
              ),
            )}
          </Box>
        </Stack>

        {/* Always beside the grid: the selected one's states, then its code. */}
        <Stack
          spacing={3}
          sx={{
            width: 560,
            flexShrink: 0,
            overflowY: 'auto',
            p: 3,
            bgcolor: 'background.paper',
            borderLeft: 1,
            borderColor: 'divider',
          }}
        >
          {!current ? (
            <Stack
              sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                Select a component to see its details
              </Typography>
            </Stack>
          ) : (
            <>
              <Typography variant='h3'>{current.name}</Typography>
              {current.note && (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {current.note}
                </Typography>
              )}
              {current.states.map((s) => (
                <Stack key={s.label} spacing={1.5}>
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary' }}
                  >
                    {s.label}
                  </Typography>
                  <Box sx={{ width: PHONE_W }}>{s.node}</Box>
                </Stack>
              ))}
              {/* Full height, not squeezed to fit: the panel scrolls instead. */}
              {current.file && (
                <Box sx={{ flexShrink: 0 }}>
                  <CodeView
                    key={current.name}
                    current={asInspection(current.name)}
                    file={current.file}
                    markupOnly
                  />
                </Box>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </IntakeStyleContext.Provider>
  );
};

export default ComponentsPage;
